import React, { useRef, useState, useEffect } from "react";
import { Target, Heart, Award, Lightbulb, Globe, Users } from "lucide-react";

import {
  motion,
  useMotionValue,
  useSpring,
  useInView,
  useMotionValueEvent,
} from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import JoinSection from "../components/JoinSection";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";

const Button = ({ children, className = "", ...props }) => (
  <button
    className={`px-4 py-2 rounded font-semibold transition inline-flex items-center justify-center ${className}`}
    {...props}
  >
    {children}
  </button>
);

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-lg border border-gray-300 bg-white text-black shadow ${className}`}
  >
    {children}
  </div>
);

const Image = ({ src, alt, className = "", ...props }) => (
  <motion.img
    src={src}
    alt={alt}
    className={className}
    whileHover={{ scale: 1.03 }}
    transition={{ duration: 0.3 }}
    {...props}
  />
);

const CardContent = ({ children, className = "" }) => (
  <div className={className}>{children}</div>
);

const Badge = ({ children, className = "" }) => (
  <span
    className={`inline-flex items-center px-3 py-1 text-sm font-semibold rounded-md ${className}`}
  >
    {children}
  </span>
);

/* ===========================
            PAGE MEMBERS
=========================== */

function AnimatedNumber({ value }) {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true });

  const numericValue = parseInt(value.replace(/\D/g, ""), 10);
  const suffix = value.replace(/[0-9]/g, "");

  const motionValue = useMotionValue(0);
  const springValue = useSpring(motionValue, {
    stiffness: 40,
    damping: 20,
  });

  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      motionValue.set(numericValue);
    }
  }, [isInView, numericValue, motionValue]);

  useMotionValueEvent(springValue, "change", (latest) => {
    setDisplayValue(Math.round(latest));
  });

  return (
    <span ref={ref}>
      {displayValue}
      {suffix}
    </span>
  );
}

export default function MembersPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    const shouldScroll = searchParams.get("join");

    if (shouldScroll === "true") {
      const timer = setTimeout(() => {
        const form = document.getElementById("join-form");
        if (form) {
          form.scrollIntoView({
            behavior: "smooth",
            block: "start",
          });
        }
      }, 300);

      return () => clearTimeout(timer);
    }
  }, [searchParams]);

  const memberSpotlight = [
  {
    name: "Mourad Boumsesse",
    role: t("members.roles.president"),
    image: "./images/mourade.jpeg",
    alt: "PRÉSIDENT",
  },
  {
    name: "Basma Salem",
    role: t("members.roles.vicePresident1"),
    image: "./images/basma.webp",
    alt: "VICE PRÉSIDENT 1",
  },
  {
    name: "Amine Mezane",
    role: t("members.roles.vicePresident2"),
    image: "./images/amin.jpg",
    alt: "VICE PRÉSIDENT 2",
  },
  {
    name: "Hamza Oubrahim",
    role: t("members.roles.secretaryGeneral"),
    image: "./images/hamza2.jpeg",
    alt: "SECRÉTAIRE GENERAL",
  },
  {
    name: "Douae Hedioui",
    role: t("members.roles.viceSecretaryGeneral"),
    image: "./images/douae.webp",
    alt: "VICE SECRÉTAIRE GENERAL",
  },
  {
    name: "Mostapha Layati",
    role: t("members.roles.treasurer"),
    image: "./images/mostapha.webp",
    alt: "TRÉSORIER",
  },
  {
    name: "Hala Abid",
    role: t("members.roles.viceTreasurer"),
    image: "./images/hala.jpeg",
    alt: "VICE TRÉSORIER",
  },
  {
    name: "Aya Touil",
    role: t("members.roles.advisorFemale"),
    image: "./images/aya.jpeg",
    alt: "CONSEILLÉRE",
  },
  {
    name: "Ziad Maaoui",
    role: t("members.roles.advisor"),
    image: "./images/ziad.jpeg",
    alt: "CONSEILLER",
  },
  {
    name: "Adam Charradi",
    role: t("members.roles.digitalManager"),
    image: "./images/adam.jpeg",
    alt: "RESPONSABLE DIGITAL",
  }
];

  const benefits = [
    {
      icon: Users,
      title: t("members.benefits.network.title"),
      description: t("members.benefits.network.description"),
    },
    {
      icon: Target,
      title: t("members.benefits.skills.title"),
      description: t("members.benefits.skills.description"),
    },
    {
      icon: Heart,
      title: t("members.benefits.impact.title"),
      description: t("members.benefits.impact.description"),
    },
    {
      icon: Award,
      title: t("members.benefits.recognition.title"),
      description: t("members.benefits.recognition.description"),
    },
    {
      icon: Lightbulb,
      title: t("members.benefits.innovation.title"),
      description: t("members.benefits.innovation.description"),
    },
    {
      icon: Globe,
      title: t("members.benefits.international.title"),
      description: t("members.benefits.international.description"),
    },
  ];

  const stats = [
    {
      number: "100+",
      label: t("members.stats.activeMembers"),
    },
    {
      number: "02+",
      label: t("members.stats.partnerships"),
    },
    {
      number: "10+",
      label: t("members.stats.activities"),
    },
    {
      number: "95%",
      label: t("members.stats.satisfaction"),
    },
  ];

  const [currentIndex, setCurrentIndex] = useState(0);

  const total = memberSpotlight.length;
  const radius = 380;

  const normalizeIndex = (index) => {
    if (index < 0) return total - 1;
    if (index >= total) return 0;
    return index;
  };

  const handleNext = () => {
    setCurrentIndex((prev) => normalizeIndex(prev + 1));
  };

  const handlePrev = () => {
    setCurrentIndex((prev) => normalizeIndex(prev - 1));
  };

  const getCircularStyle = (index) => {
    const angle = ((index - currentIndex) * 360) / total;
    const rad = (angle * Math.PI) / 180;

    const x = Math.sin(rad) * radius;
    const z = Math.cos(rad) * radius;
    const scale = z > 0 ? 1 : 0.75;
    const opacity = z > 0 ? 1 : 0.35;
    const blur = z > 0 ? 0 : 2;

    return {
      transform: `
      translateX(${x}px)
      translateZ(${z}px)
      scale(${scale})
    `,
      opacity,
      filter: `blur(${blur}px)`,
      zIndex: Math.round(z),
    };
  };
  const [isDragging, setIsDragging] = useState(false);
  useEffect(() => {
    if (isDragging) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }

    return () => {
      document.body.style.overflow = "";
    };
  }, [isDragging]);
  useEffect(() => {
    if (isDragging) return;

    const interval = setInterval(() => {
      handleNext();
    }, 4500);

    return () => clearInterval(interval);
  }, [currentIndex, isDragging]);

  return (
    <div className="min-h-screen bg-background">
      <section className="py-20 bg-blue-50">
        <div className="px-4 sm:px-6 overflow-x-hidden">
          <motion.div
            className="text-center mb-12 sm:mb-16"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h1
              className="
      font-heading 
      text-3xl 
      sm:text-5xl 
      md:text-6xl 
      font-black 
      text-foreground 
      mb-4 
      sm:mb-6 
      leading-snug sm:leading-tight
      break-words
    "
            >
              {t("members.hero.title1")}{" "}
              <span className="text-primary">
                {" "}
                {t("members.hero.highlight")}
              </span>
            </h1>

            <p
              className="
      font-paragraph 
      text-base 
      sm:text-xl 
      text-foreground/80 
      max-w-xl 
      mx-auto 
      leading-relaxed
    "
            >
              {t("members.hero.description")}
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-16 bg-foreground text-background">
        <div className="container mx-auto px-6 max-w-[100rem]">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            {stats.map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <div className="font-heading text-3xl sm:text-4xl font-bold text-electric-blue">
                  <AnimatedNumber value={stat.number} />
                </div>

                <div className="font-paragraph text-background/80">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-background">
        <div className="container max-w-[100rem] mx-auto px-6">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-6">
                {t("members.benefits.sectionTitle1")}{" "} <span className="text-primary"> {t("members.benefits.sectionHighlight")}</span>
            </h2>
            <p className="font-paragraph text-lg sm:text-xl text-foreground/80 max-w-3xl mx-auto">
              {t("members.benefits.sectionDescription")}
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="p-6 h-full hover:shadow-xl transition-shadow duration-300">
                  <CardContent>
                    <benefit.icon className="h-12 w-12 text-primary mb-4" />
                    <h3 className="font-heading text-xl font-bold text-foreground mb-2">
                      {benefit.title}
                    </h3>
                    <p className="font-paragraph text-foreground/80">
                      {benefit.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-50 overflow-hidden">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 relative text-center">
          <motion.div
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl sm:text-5xl font-bold text-foreground mb-6">
              {t("members.spotlight.title1")}{" "}<span className="text-electric-blue">{t("members.spotlight.highlight")}</span>
            </h2>
            <p className="font-paragraph text-lg sm:text-xl text-foreground/80 max-w-3xl mx-auto">
              {t("members.spotlight.description")}
            </p>
          </motion.div>

          <div className="relative flex justify-center mt-10 mb-2 pointer-events-none">
            <svg
              width="800"
              height="160"
              viewBox="0 0 800 160"
              className="max-w-full"
            >
              <path
                id="arc-path"
                d="M 100 140 Q 400 60 700 140"
                fill="transparent"
              />

              <text
                fill="#d1d5db"
                fontSize="70"
                fontWeight="800"
                letterSpacing="14"
              >
                <textPath
                  href="#arc-path"
                  startOffset="50%"
                  textAnchor="middle"
                >
                   {t("members.spotlight.team")}
                </textPath>
              </text>
            </svg>
          </div>

          <button
            onClick={handlePrev}
            className="hidden sm:flex absolute left-0 top-1/2 -translate-y-1/2 translate-y-20 z-50
  w-11 h-11 rounded-full bg-blue-200 shadow
  items-center justify-center hover:scale-110 transition"
          >
            <ChevronLeft />
          </button>

          <button
            onClick={handleNext}
            className="hidden sm:flex absolute right-0 top-1/2 -translate-y-1/2 translate-y-20 z-50
  w-11 h-11 rounded-full bg-blue-200 shadow
  items-center justify-center hover:scale-110 transition"
          >
            <ChevronRight />
          </button>

          <div className="relative mx-auto h-[280px] sm:h-[320px]">
            <div
              className="relative h-full flex items-center justify-center"
              style={{ perspective: "1200px" }}
            >
              {memberSpotlight.map((member, index) => {
                const total = memberSpotlight.length;

                let pos = index - currentIndex;
                if (pos > total / 2) pos -= total;
                if (pos < -total / 2) pos += total;

                const level = Math.abs(pos);
                const isMobile = window.innerWidth < 640;

                const overlapX = isMobile ? 90 : 120;
                const overlapY = isMobile ? 10 : 14;

                const x = pos * overlapX;
                const y = level * overlapY;
                const z = -level * (isMobile ? 80 : 130);

                return (
                  <motion.div
                    key={index}
                    drag="x"
                    dragConstraints={{ left: 0, right: 0 }}
                    dragElastic={isMobile ? 0.35 : 0.15}
                    onDragEnd={(e, info) => {
                      if (info.offset.x < -40) handleNext();
                      if (info.offset.x > 40) handlePrev();
                    }}
                    animate={{
                      x,
                      y,
                      z,
                      zIndex: 100 - level,
                    }}
                    transition={{
                      type: "spring",
                      stiffness: 240,
                      damping: 26,
                    }}
                    className="absolute cursor-grab active:cursor-grabbing"
                    style={{ transformStyle: "preserve-3d" }}
                  >
                    <div className="relative w-44 h-60 sm:w-60 sm:h-80 rounded-xl bg-white shadow-xl overflow-hidden">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </motion.div>
                );
              })}
            </div>
          </div>

          <div className="mt-8">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900">
              {memberSpotlight[currentIndex].name}
            </h3>
            <div className="flex items-center justify-center gap-3 mt-2">
              <span className="w-12 h-px bg-primary"></span>
              <p className="uppercase tracking-widest text-xs sm:text-sm text-primary font-semibold">
                {memberSpotlight[currentIndex].role}
              </p>
              <span className="w-12 h-px bg-primary"></span>
            </div>
          </div>

          <div className="flex justify-center gap-2 mt-6">
            {memberSpotlight.map((_, i) => (
              <span
                key={i}
                className={`w-2 h-2 rounded-full transition ${
                  i === currentIndex ? "bg-primary scale-110" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
        </div>
      </section>

      <JoinSection />

      <section className="py-20 bg-gradient-to-r from-secondary to-primary text-white">
        <div className="container max-w-[100rem] mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
          >
            <h2 className="font-heading text-3xl sm:text-4xl font-bold mb-6">
              {t("members.cta.title")}
            </h2>

            <p className="font-paragraph text-lg sm:text-xl mb-8 max-w-3xl mx-auto">
               {t("members.cta.description")}
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="border-white text-white hover:bg-white hover:text-primary">
                <Link to="/contact"> {t("members.cta.contact")}</Link>
              </Button>

              <Button className="border-white text-white hover:bg-white hover:text-primary">
                <Link to="/about">{t("members.cta.learnMore")}</Link>
              </Button>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
